import type { SDKMessage, SDKToolUseMessage } from "../../types/messages.js";
import { emitRunEvent } from "../../types/run-events.js";
import { generateCallId } from "../ids.js";
import type { LlmContentPart, LlmToolCallPart } from "../llm/types.js";
import { checkToolWhitelist } from "../runtime/concurrency/async-local-storage.js";
import { mapWithConcurrency } from "../runtime/concurrency/map-with-concurrency.js";
import { HISTOGRAM_NAMES } from "../telemetry/span-names.js";
import { type RepairableTool, repairToolCall } from "../tool-dispatch/repair-middleware.js";
import type { AgentLoopInputs, ResolvedTool } from "./loop-types.js";
import { executeTool, renderToolResult, type ToolResult } from "./tool-executors.js";
import { raceToolExecution } from "./tool-timeout.js";

export type { ResolvedTool } from "./loop-types.js";

/**
 * T2.4 — Parallel tool dispatch with bounded concurrency (DR2 finding #4).
 *
 * Pre-T2.4 this was a serial `for...of` loop — each tool call awaited
 * before the next. With N independent tools each taking Tms, total
 * wall-clock was N×Tms. T2.4 switches to `Promise.all` with bounded
 * concurrency via an inline semaphore (ADR D135 — in-house, no
 * `p-limit` dep). Default cap is 4 concurrent tools; consumer
 * overrides via `AgentLoopInputs.maxConcurrentTools`.
 *
 * Order preservation: `Promise.all` preserves input order in the
 * resolved array regardless of which promise settles first — the
 * LLM expects tool results in call order and this contract holds.
 *
 * Event array safety: `events` is append-only. In single-threaded
 * JS (Node event loop), concurrent `.push()` calls from different
 * microtask continuations do NOT interleave — each push is atomic.
 * The order of events across tools becomes non-deterministic, but
 * each individual tool's events are internally ordered.
 */
export async function dispatchTools(
  inputs: AgentLoopInputs,
  tools: ResolvedTool[],
  toolCalls: LlmToolCallPart[],
  events: SDKMessage[],
  // M3 #64 — the run's agent.send span, so each tool.call span nests under it.
  parentSpan?: ToolSpan,
): Promise<LlmContentPart[]> {
  const maxConcurrent = (inputs as { maxConcurrentTools?: number }).maxConcurrentTools ?? 4;
  // M0-2: consolidated onto the shared ordered bounded pool (was a private
  // `boundedParallel` clone — see plan m0-foundation-expose-primitives).
  return mapWithConcurrency(toolCalls, maxConcurrent, (call) =>
    dispatchSingleCall(inputs, tools, call, events, parentSpan),
  );
}

/** M3 #64 — the OTel span type threaded for tool-span nesting. */
type ToolSpan = ReturnType<NonNullable<AgentLoopInputs["telemetry"]>["startSpan"]> | undefined;

/**
 * T10.4 / PV#2 — `dispatchSingleCall` orchestrator split into named sub-steps.
 * Each step is a private function with a single concern; the orchestrator
 * sequences them. The previous complexity-suppression directive (over a
 * 158 LOC body with 5 origin branches inline) is no longer required after
 * the split.
 *
 * Step taxonomy:
 *  1. `applyRepairAndExtractCall` — ADRs D86-D88 repair middleware.
 *  2. `enforceForkWhitelist`      — ADR D111 fork tool-whitelist veto.
 *  3. `startToolCallSpan`         — OTel span init + repairs annotation.
 *  4. `runPluginPreToolVeto`      — ADR D101 plugin veto.
 *  5. `runFileHookPreToolVeto`    — operator-policy hook veto.
 *  6. `runToolWithLifecycle`      — exec + onToolStart/End/Error hooks.
 *  7. `finalizeSpanAndPostHook`   — span end + postToolUse + return shape.
 */
async function dispatchSingleCall(
  inputs: AgentLoopInputs,
  tools: ResolvedTool[],
  call: LlmToolCallPart,
  events: SDKMessage[],
  parentSpan?: ToolSpan,
): Promise<LlmContentPart> {
  const { call: workingCall, repairs } = applyRepairAndExtractCall(tools, call);
  const callId = generateCallId();

  const forkVeto = vetoFromForkWhitelist(inputs, workingCall, callId, events);
  if (forkVeto !== undefined) return forkVeto;

  const resolved = tools.find((tool) => tool.name === workingCall.name);
  const toolSpan = startToolCallSpan(inputs, workingCall, resolved, callId, repairs, parentSpan);
  events.push(buildToolUseRunning(inputs, callId, workingCall));

  const pluginVeto = await vetoFromPluginPreHook(inputs, workingCall, callId, events);
  if (pluginVeto !== undefined) {
    // T2.5 — end the span on veto so we don't leak open OTel spans.
    // Pre-T2.5 the span started at step 3 but veto returns at step 4/5
    // skipped step 7's `toolSpan.end()` — leaked an open span.
    toolSpan?.setAttribute("tool.vetoed", true);
    toolSpan?.setAttribute("tool.veto_source", "plugin");
    toolSpan?.end();
    return pluginVeto;
  }

  const fileVeto = await vetoFromFileHookPreDecision(inputs, workingCall, callId, events);
  if (fileVeto !== undefined) {
    toolSpan?.setAttribute("tool.vetoed", true);
    toolSpan?.setAttribute("tool.veto_source", "file_hook");
    toolSpan?.end();
    return fileVeto;
  }

  // SE2 — all vetoes passed; the tool is about to dispatch. Emit tool_progress.
  emitRunEvent(inputs.runEventSink, {
    type: "tool_progress",
    toolName: workingCall.name,
    toolCallId: callId,
  });
  const result = await runToolWithLifecycle(inputs, resolved, workingCall, callId);
  // #65 — post_tool_call hook (previously dead) fires after each tool completes.
  await inputs.pluginManager?.runPostToolCallHooks({
    name: workingCall.name,
    args: workingCall.input,
    result: { stdout: result.stdout, stderr: result.stderr, exitCode: result.exitCode },
    agentId: inputs.agentId,
    runId: inputs.runId,
  });
  return finalizeSpanAndPostHook(inputs, workingCall, callId, result, events, toolSpan);
}

interface RepairedCallShape {
  call: LlmToolCallPart;
  repairs: ReadonlyArray<string>;
}

/**
 * Step 1 — D86-D88 repair middleware. Returns the (possibly rewritten) call
 * and the list of repairs applied (used later for span annotation).
 */
function applyRepairAndExtractCall(
  tools: ResolvedTool[],
  call: LlmToolCallPart,
): RepairedCallShape {
  const registryMap = buildRepairRegistry(tools);
  const repaired = repairToolCall({ name: call.name, args: call.input, id: call.id }, registryMap);
  if (repaired.repairs.length === 0) return { call, repairs: repaired.repairs };
  return {
    call: {
      ...call,
      name: repaired.call.name,
      input: (repaired.call.args ?? {}) as Record<string, unknown>,
    },
    repairs: repaired.repairs,
  };
}

/**
 * Step 2 — D111 fork whitelist gate. Fires BEFORE plugin/file hooks because
 * a fork's allowedTools set is the strictest contract. Returns the
 * early-return content part when blocked; `undefined` to continue.
 */
function vetoFromForkWhitelist(
  inputs: AgentLoopInputs,
  call: LlmToolCallPart,
  callId: string,
  events: SDKMessage[],
): LlmContentPart | undefined {
  const whitelistDecision = checkToolWhitelist(call.name);
  if (whitelistDecision.allowed) return undefined;
  // SE2 — fork-whitelist denial is also an observable permission_denied.
  emitRunEvent(inputs.runEventSink, {
    type: "permission_denied",
    toolName: call.name,
    toolCallId: callId,
    source: "fork_whitelist",
    message: whitelistDecision.reason ?? "tool not available in fork",
  });
  events.push(buildToolUseRunning(inputs, callId, call));
  events.push(
    buildToolUseCompleted(inputs, callId, call, {
      stdout: "",
      stderr: whitelistDecision.reason ?? "tool not available in fork",
      exitCode: 126,
    }),
  );
  return {
    type: "tool_result",
    toolUseId: call.id,
    content: `Tool blocked by fork whitelist: ${whitelistDecision.reason}`,
  };
}

/**
 * Step 3 — OTel span init + repairs/args annotation. Returns the span
 * handle (or `undefined` when telemetry is disabled) for later finalization.
 */
function startToolCallSpan(
  inputs: AgentLoopInputs,
  call: LlmToolCallPart,
  resolved: ResolvedTool | undefined,
  callId: string,
  repairs: ReadonlyArray<string>,
  parentSpan?: ToolSpan,
): ToolSpan {
  // M3 #64 — nest tool.call under the run's agent.send span (not a flat sibling).
  const toolSpan = inputs.telemetry?.startChildSpan(parentSpan, "tool.call", {
    "tool.name": call.name,
    "tool.origin": resolved?.origin ?? "unknown",
    callId,
  });
  if (repairs.length > 0 && toolSpan !== undefined) {
    toolSpan.setAttribute("tool.repairs", repairs.join("; "));
  }
  if (toolSpan !== undefined && inputs.telemetry?.includeContent === true) {
    toolSpan.addEvent("args", { input: JSON.stringify(call.input) });
  }
  return toolSpan;
}

/**
 * Step 4 — D101 plugin `pre_tool_call` veto. Plugins are author-supplied
 * (code-level safety) and fire BEFORE file hooks (operator policy).
 */
async function vetoFromPluginPreHook(
  inputs: AgentLoopInputs,
  call: LlmToolCallPart,
  callId: string,
  events: SDKMessage[],
): Promise<LlmContentPart | undefined> {
  const pluginVeto = await inputs.pluginManager?.runPreToolCallHooks({
    name: call.name,
    args: call.input,
    agentId: inputs.agentId,
    runId: inputs.runId,
  });
  if (pluginVeto === undefined) return undefined;
  // SE2 — a plugin veto (e.g. the permission gate) is a runtime-observability
  // signal: emit a typed `permission_denied` event out-of-band.
  emitRunEvent(inputs.runEventSink, {
    type: "permission_denied",
    toolName: call.name,
    toolCallId: callId,
    source: "plugin",
    message: pluginVeto.message,
  });
  events.push(
    buildToolUseCompleted(inputs, callId, call, {
      stdout: "",
      stderr: pluginVeto.message,
      exitCode: 126,
    }),
  );
  return {
    type: "tool_result",
    toolUseId: call.id,
    content: `Plugin blocked this tool call: ${pluginVeto.message}`,
  };
}

/**
 * Step 5 — file-based hooks `preToolUse` decision. Denial is surfaced as a
 * regular `tool_result` (not `isError`) so the model can react in narrative;
 * marking it `isError` would short-circuit the agent loop, which is too
 * harsh for a policy denial.
 */
async function vetoFromFileHookPreDecision(
  inputs: AgentLoopInputs,
  call: LlmToolCallPart,
  callId: string,
  events: SDKMessage[],
): Promise<LlmContentPart | undefined> {
  const preDecision = await inputs.hooks.run({
    event: "preToolUse",
    tool: call.name,
    input: call.input,
    agentId: inputs.agentId,
    runId: inputs.runId,
  });
  if (!preDecision.blocked) return undefined;
  // SE2 — operator file-hook `preToolUse` denial is also an observable event.
  emitRunEvent(inputs.runEventSink, {
    type: "permission_denied",
    toolName: call.name,
    toolCallId: callId,
    source: "file_hook",
    message: preDecision.reason ?? "blocked by hook",
  });
  events.push(
    buildToolUseCompleted(inputs, callId, call, {
      stdout: "",
      stderr: preDecision.reason ?? "blocked by hook",
      exitCode: 126,
    }),
  );
  return {
    type: "tool_result",
    toolUseId: call.id,
    content: `Hook denied this tool call: ${preDecision.reason ?? "no reason given"}`,
  };
}

/**
 * Step 6 — exec + D315-D317 onToolStart/End/Error lifecycle. Fires
 * `onToolError` when stderr/exit signals failure; otherwise `onToolEnd`.
 * The `error` field passed to `onToolError` is ALWAYS an `Error` instance.
 */
async function runToolWithLifecycle(
  inputs: AgentLoopInputs,
  resolved: ResolvedTool | undefined,
  call: LlmToolCallPart,
  callId: string,
): Promise<ToolResult> {
  const startAt = Date.now();
  await safeEmitToolHook(inputs.onToolStart, {
    toolName: call.name,
    args: call.input,
    conversationId: inputs.agentId,
    callId,
  });
  // #58 — bound the tool by the run's cancellation signal + optional per-tool
  // timeout so a hung tool cannot wedge the loop and cancel interrupts it.
  const result = await raceToolExecution(executeTool(inputs, resolved, call), {
    signal: inputs.signal,
    timeoutMs: inputs.perToolTimeoutMs,
  });
  const durationMs = Date.now() - startAt;
  // M3 #64 — emit the tool-call duration as a metric (was hook-only).
  inputs.telemetry?.recordHistogram(HISTOGRAM_NAMES.TOOL_CALL_DURATION_MS, durationMs, {
    "tool.name": call.name,
  });
  if (result.exitCode !== undefined && result.exitCode !== 0 && result.exitCode !== null) {
    await safeEmitToolHook(inputs.onToolError, {
      toolName: call.name,
      args: call.input,
      error: new Error(result.stderr || `tool exited with code ${result.exitCode}`),
      conversationId: inputs.agentId,
      callId,
      durationMs,
      attempt: 1,
    });
  } else {
    await safeEmitToolHook(inputs.onToolEnd, {
      toolName: call.name,
      args: call.input,
      // SE7 — a block-returning handler has empty `stdout`; surface its structured
      // content to the hook instead of an empty string.
      result: result.content !== undefined ? result.content : result.stdout,
      conversationId: inputs.agentId,
      callId,
      durationMs,
    });
  }
  return result;
}

/**
 * Step 7 — finalize the OTel span, emit the `tool_use_completed` event,
 * fire the `postToolUse` hook fire-and-forget, and build the
 * `tool_result` content part returned to the agent loop.
 */
function finalizeSpanAndPostHook(
  inputs: AgentLoopInputs,
  call: LlmToolCallPart,
  callId: string,
  result: ToolResult,
  events: SDKMessage[],
  toolSpan: ReturnType<NonNullable<AgentLoopInputs["telemetry"]>["startSpan"]> | undefined,
): LlmContentPart {
  toolSpan?.setAttribute("exitCode", result.exitCode ?? 0);
  if (toolSpan !== undefined && inputs.telemetry?.includeContent === true) {
    toolSpan.addEvent("result", { stdout: result.stdout.slice(0, 1000) });
  }
  toolSpan?.end();
  events.push(buildToolUseCompleted(inputs, callId, call, result));
  // T2.8 — postToolUse is fire-and-forget (runs AFTER the tool result
  // is captured, so a veto is meaningless). But errors must NOT be
  // silently swallowed — at minimum log WARN so operators know a hook
  // is misbehaving. Pre-T2.8 the bare `void` prefix lost errors entirely.
  inputs.hooks
    .run({
      event: "postToolUse",
      tool: call.name,
      input: call.input,
      output: {
        stdout: result.stdout,
        stderr: result.stderr,
        exitCode: result.exitCode ?? 0,
        // SE7 — structured content (text + image blocks) when the handler
        // returned/threw blocks; absent for the string path.
        ...(result.content !== undefined ? { content: result.content } : {}),
      },
      agentId: inputs.agentId,
      runId: inputs.runId,
    })
    .catch((err: unknown) => {
      process.stderr.write(
        `[theokit-sdk] postToolUse hook error (swallowed): ${err instanceof Error ? err.message : String(err)}\n`,
      );
    });
  return {
    type: "tool_result",
    toolUseId: call.id,
    // SE7 — structured content blocks (handler-returned or ToolError-carried)
    // are authoritative; otherwise the legacy string rendering.
    content: result.content !== undefined ? result.content : renderToolResult(result),
    ...(result.exitCode !== 0 && result.exitCode !== undefined ? { isError: true } : {}),
  };
}

/**
 * D317 — fire-and-forget tool-lifecycle hook with error swallowed via
 * stderr warn. Single chokepoint so all three callbacks (start/end/error)
 * share the same hardening: a misbehaving listener cannot crash the agent
 * loop.
 *
 * @internal
 */
async function safeEmitToolHook<E>(
  callback: ((event: E) => void | Promise<void>) | undefined,
  event: E,
): Promise<void> {
  if (callback === undefined) return;
  try {
    await callback(event);
  } catch (cause) {
    const msg = cause instanceof Error ? cause.message : String(cause);
    process.stderr.write(`[theokit-sdk] tool lifecycle hook threw: ${msg}\n`);
  }
}

function buildToolUseRunning(
  inputs: AgentLoopInputs,
  callId: string,
  call: LlmToolCallPart,
): SDKToolUseMessage {
  return {
    type: "tool_call",
    agent_id: inputs.agentId,
    run_id: inputs.runId,
    call_id: callId,
    name: call.name,
    status: "running",
    args: call.input,
  };
}

/**
 * T4.1 helper: project the agent-loop's ResolvedTool[] into a registry
 * shape consumable by `repairToolCall`. Caller owns the Map lifetime
 * (rebuilt each dispatchSingleCall — O(tools.length) overhead is negligible
 * compared to the LLM round-trip).
 */
function buildRepairRegistry(tools: ResolvedTool[]): ReadonlyMap<string, RepairableTool> {
  const out = new Map<string, RepairableTool>();
  for (const t of tools) {
    out.set(t.name, { name: t.name, inputSchema: t.inputSchema });
  }
  return out;
}

function buildToolUseCompleted(
  inputs: AgentLoopInputs,
  callId: string,
  call: LlmToolCallPart,
  result: ToolResult,
): SDKToolUseMessage {
  return {
    type: "tool_call",
    agent_id: inputs.agentId,
    run_id: inputs.runId,
    call_id: callId,
    name: call.name,
    status: "completed",
    args: call.input,
    result: {
      stdout: result.stdout,
      stderr: result.stderr,
      exitCode: result.exitCode ?? 0,
    },
  };
}
